import styled from "@emotion/styled";
import { FC, useState } from "react";
import { Field, FieldRenderProps, FormSpy, useForm } from "react-final-form";
import { OnChange } from "react-final-form-listeners";
import { useSelector } from "react-redux";

import {
  DesignToken,
  Heading,
  IPickerListItem,
  Picker,
  RadioButton,
  SpacingToken,
  Switch,
  Text,
  Tooltip,
} from "@livechat/design-system-react-components";

import { AutoAssignmentInfoTooltip } from "components/autoassignment/AutoAssignmentInfoTooltip";
import Flex from "components/layout/Flex";
import { mapTeamsToPickerItems } from "components/picker-items/teamPickerItems";
import Role from "constants/role";
import { agentCanBeAssignedToTickets } from "helpers/agent";
import { FormFieldNoMargin } from "routes/settings/inboxes/FormFieldNoMargin";
import { IAgent } from "store/entities/agents";
import { selectCanChangeAutoassignment } from "store/entities/license";
import { selectTeams } from "store/entities/teams";
import { withVar } from "themes/helpers";
import { areSimilar } from "utils/array";

import { AgentFormSection } from "./AgentFormSection";
import { AgentFormSectionId } from "./constants";

export const AutoAssignmentManagement: FC = () => {
  const form = useForm<IAgent>();

  const canChangeAutoassignment = useSelector(selectCanChangeAutoassignment);

  const isEditingViewer = (values: IAgent): boolean => values.permission === Role.Viewer;
  const isAutoassignmentDisallowed = (values: IAgent): boolean => !agentCanBeAssignedToTickets(values);
  const isDisabled = (values: IAgent): boolean => !canChangeAutoassignment || isAutoassignmentDisallowed(values);

  const onRoleChange = (value: Role, prev: Role): void => {
    if (value !== Role.Viewer && prev === Role.Viewer) {
      const autoassignmentState = form.getFieldState("autoassignment");
      if (!autoassignmentState?.modified) {
        form.change("autoassignment", true);
      }
    }
  };

  return (
    <>
      <OnChange name="permission">{onRoleChange}</OnChange>
      <FormSpy<IAgent> subscription={{ values: true }}>
        {({ values }) => (
          <Tooltip
            placement="top"
            kind="invert"
            isVisible={canChangeAutoassignment ? false : undefined}
            triggerRenderer={() => (
              <AgentFormSection
                header={{
                  id: AgentFormSectionId.AutoAssignment,
                  component: (
                    <>
                      <Field<boolean> name="autoassignment" type="checkbox" value={values.autoassignment}>
                        {({ input }) => (
                          <Switch
                            disabled={isDisabled(values)}
                            data-checked={input.value}
                            data-testid="auto-assignment-switch"
                            on={isAutoassignmentDisallowed(values) ? false : input.value}
                            onChange={() => {
                              form.mutators.setFieldTouched("autoassignment", true);
                              input.onChange(!input.value);
                            }}
                            size="medium"
                          />
                        )}
                      </Field>
                      <FlexHeading
                        size="xs"
                        customColor={isDisabled(values) ? withVar(DesignToken.ContentBasicDisabled) : undefined}
                        as="span"
                        id="autoassignment-section"
                      >
                        Auto-assignment
                        <AutoAssignmentInfoTooltip location="agent" showOnHover={canChangeAutoassignment} />
                      </FlexHeading>
                    </>
                  ),
                }}
              >
                {!isAutoassignmentDisallowed(values) && values.autoassignment ? (
                  <Flex column gap={withVar(SpacingToken.Spacing5)}>
                    <Text
                      customColor={withVar(
                        isDisabled(values) ? DesignToken.ContentBasicDisabled : DesignToken.ContentBasicSecondary
                      )}
                    >
                      Specify settings for ticket auto-assignment. New agents or admins added to the team will have
                      auto-assignment accepted by default.
                    </Text>
                    <Field<string[]> name="autoassignmentTeamIDs" isEqual={areSimilar}>
                      {props => (
                        <AcceptedTeamsInput {...props} agentTeamIds={values.teamIds} disabled={isDisabled(values)} />
                      )}
                    </Field>
                  </Flex>
                ) : (
                  <NotAcceptingMessage
                    isDisabled={isDisabled(values)}
                    isEditingViewer={isEditingViewer(values)}
                    isEditingInactiveUser={values.status === "invited"}
                  />
                )}
              </AgentFormSection>
            )}
          >
            Only admins can change the auto-assignment settings.
          </Tooltip>
        )}
      </FormSpy>
    </>
  );
};

const NotAcceptingMessage: FC<{
  isEditingViewer: boolean;
  isDisabled: boolean;
  isEditingInactiveUser: boolean;
}> = ({ isEditingViewer, isDisabled, isEditingInactiveUser }) => {
  return (
    <Text customColor={withVar(isDisabled ? DesignToken.ContentBasicDisabled : DesignToken.ContentBasicSecondary)}>
      {isEditingViewer || isEditingInactiveUser ? (
        <>
          {isEditingViewer ? "Viewers" : "Users without an activated account"} can&apos;t be auto-assigned to tickets.
        </>
      ) : (
        <>The auto-assignment of tickets is deactivated and the agent won&apos;t be auto-assigned to any tickets.</>
      )}
    </Text>
  );
};

enum PickedTeamsRadio {
  AcceptAll,
  AcceptSpecific,
}
const AcceptedTeamsInput: FC<
  FieldRenderProps<string[]> & {
    agentTeamIds: string[] | null;
    disabled: boolean;
  }
> = ({ input, meta, agentTeamIds, disabled }) => {
  const { value, onChange } = input;
  const allTeams = useSelector(selectTeams);

  const availableTeams = Object.values(allTeams).filter(team => agentTeamIds?.includes(team.id));

  const availableTeamsIds = availableTeams.map(team => team.id);
  const wasAcceptingAll = value.length === availableTeamsIds.length;

  const [selectedRadio, setSelectedRadio] = useState<PickedTeamsRadio>(
    wasAcceptingAll ? PickedTeamsRadio.AcceptAll : PickedTeamsRadio.AcceptSpecific
  );

  const [preservedTeams, setPreservedTeams] = useState<string[]>(value || []);

  const teamsOptions = mapTeamsToPickerItems(availableTeams);

  const handleTeamsSelect = (selectedTeams: IPickerListItem[] | null): void => {
    const teamIds = selectedTeams?.map(team => team.key) || [];
    if (selectedTeams === null) {
      onChange([]);
    } else {
      onChange(teamIds);
    }
    setPreservedTeams(teamIds);
  };

  const handleAcceptAll = (): void => {
    onChange(availableTeamsIds);
    setSelectedRadio(PickedTeamsRadio.AcceptAll);
  };

  const handleAcceptSpecific = (): void => {
    onChange(preservedTeams);
    setSelectedRadio(PickedTeamsRadio.AcceptSpecific);
  };

  return (
    <Flex column gap={withVar(SpacingToken.Spacing4)}>
      <RadioButton
        checked={selectedRadio === PickedTeamsRadio.AcceptAll}
        onChange={handleAcceptAll}
        disabled={disabled}
        data-testid="accept-all-teams"
      >
        Accept for all agent teams
      </RadioButton>
      <RadioButton
        disabled={disabled}
        checked={selectedRadio === PickedTeamsRadio.AcceptSpecific}
        onChange={handleAcceptSpecific}
        data-testid="accept-specific-teams"
        description={
          <>
            When an agent is added to a new team, <b>accept auto-assignment</b> will be selected by default for this
            team.
          </>
        }
      >
        Accept only for specific agent teams
      </RadioButton>
      {selectedRadio === PickedTeamsRadio.AcceptSpecific && (
        <FormFieldNoMargin
          labelText="Select teams"
          description="Select at least one team for tickets to be auto-assigned"
        >
          <Picker
            disabled={disabled}
            type="multi"
            error={(meta?.touched || meta?.modified) && meta?.error}
            placeholder="Select one or more teams"
            selectAllOptionText="Select all"
            options={teamsOptions}
            onSelect={handleTeamsSelect}
            selected={teamsOptions.filter(team => preservedTeams.includes(team.key))}
            data-testid="accepted-teams-picker"
          />
        </FormFieldNoMargin>
      )}
    </Flex>
  );
};

const FlexHeading = styled(Heading)`
  display: flex;
  align-items: center;
  gap: ${withVar(SpacingToken.Spacing1)};
`;
